import { useEffect, useState, useCallback } from "react";
import { Bell, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

type NotificationRow = {
  id: string;
  user_id: string;
  action_code: string | null;
  title: string;
  body: string | null;
  link: string | null;
  read_at: string | null;
  created_at: string;
};

export function NotificationBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState<NotificationRow[]>([]);
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  const load = useCallback(async (uid: string) => {
    const { data } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", uid)
      .order("created_at", { ascending: false })
      .limit(20);
    setItems((data as NotificationRow[]) ?? []);
  }, []);

  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      setUserId(user.id);
      await load(user.id);

      channel = supabase
        .channel(`notifications:${user.id}`)
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
          () => load(user.id),
        )
        .subscribe();
    })();
    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, [load]);

  const unread = items.filter((i) => !i.read_at);
  const read = items.filter((i) => i.read_at);

  async function markAsRead(id: string) {
    await supabase.from("notifications").update({ read_at: new Date().toISOString() }).eq("id", id);
    if (userId) load(userId);
  }

  async function markAllRead() {
    if (!userId || unread.length === 0) return;
    await supabase
      .from("notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("user_id", userId)
      .is("read_at", null);
    load(userId);
  }

  function handleClick(n: NotificationRow) {
    if (!n.read_at) markAsRead(n.id);
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-muted transition"
          aria-label="Notificações"
        >
          <Bell className="h-5 w-5 text-foreground" />
          {unread.length > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 text-[10px] leading-none flex items-center justify-center rounded-full"
            >
              {unread.length > 9 ? "9+" : unread.length}
            </Badge>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <p className="text-sm font-semibold">Notificações</p>
          {unread.length > 0 && (
            <Button variant="ghost" size="sm" onClick={markAllRead} className="text-xs h-7">
              <Check className="h-3.5 w-3.5 mr-1" /> Marcar todas como lidas
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-[420px]">
          {items.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">
              Nenhuma notificação por enquanto.
            </div>
          )}

          {unread.length > 0 && (
            <div>
              <div className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                Não lidas
              </div>
              {unread.map((n) => (
                <NotificationItem key={n.id} item={n} onClick={() => handleClick(n)} />
              ))}
            </div>
          )}

          {read.length > 0 && (
            <div>
              <div className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                Lidas
              </div>
              {read.map((n) => (
                <NotificationItem key={n.id} item={n} onClick={() => handleClick(n)} />
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

function NotificationItem({ item, onClick }: { item: NotificationRow; onClick: () => void }) {
  const isUnread = !item.read_at;
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-muted/50 transition flex gap-3",
        isUnread && "bg-primary/5",
      )}
    >
      <div className={cn("mt-1.5 h-2 w-2 rounded-full shrink-0", isUnread ? "bg-primary" : "bg-transparent")} />
      <div className="flex-1 min-w-0">
        <p className={cn("text-sm leading-tight", isUnread ? "font-semibold" : "font-medium text-muted-foreground")}>
          {item.title}
        </p>
        {item.body && (
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{item.body}</p>
        )}
        <p className="text-[11px] text-muted-foreground mt-1">
          {formatDistanceToNow(new Date(item.created_at), { addSuffix: true, locale: ptBR })}
        </p>
      </div>
    </button>
  );
}
